import * as React from 'react';
import { Typography, Box, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  StyledBox,
  StyledButtonBox,
  StyledChip,
  StyledChipWrapper,
  StyledSelectedTeams,
} from './SelectedFavourites.styled';
import ChipDisplay from '../../shared/chips/ChipDisplay';
import CustomButton from '../../shared/button/CustomButton';
import AllSelectedTeamsModal from './AllSelectedFavouritesModal';
import CustomAccordion from '../../shared/accordion/CustomAccordion';
import { useSelectedFavourites } from '../../../zustand/SelectedFavourites';
import { MAX_AMOUNT } from '../search/Popular.constant';
import useHandleClickFavourites from '../../shared/hooks/useHandleClickFavourites';

const SelectedFavourites: React.FC = () => {
  const [open, setOpen] = React.useState<boolean>(false);
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const {
    selectedCompetitions,
    selectedTeams,
    setSelectedCompetitions,
    setSelectedTeams,
  } = useSelectedFavourites();
  const navigate = useNavigate();
  const { handleRemoveCompetition, handleRemoveTeam } =
    useHandleClickFavourites();
  const { resetTeams: resetAllTeamsandCompetitions } =
    useHandleClickFavourites();
  const selectedTeamsCount = selectedTeams.length;
  const selectedCompetitionsCount = selectedCompetitions.length;

  const displayedTeams =
    selectedTeamsCount >= MAX_AMOUNT
      ? selectedTeams.slice(0, MAX_AMOUNT)
      : selectedTeams;

  const notDisplayedTeamsCount =
    selectedTeamsCount > MAX_AMOUNT ? selectedTeamsCount - MAX_AMOUNT : 0;

  const confirmSelectionAndNextStep = () => {
    setSelectedCompetitions(selectedCompetitions);
    setSelectedTeams(selectedTeams);
    navigate('/overviewfilter');
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const toggleExpand = () => setExpanded((prev) => !prev);

  const showSelected =
    selectedCompetitions.length > 0 || selectedTeams.length > 0;

  return (
    <StyledSelectedTeams>
      {showSelected && (
        <Paper sx={{ padding: 3 }}>
          <CustomAccordion
            expanded={expanded}
            mainText={`Selected teams and competitions (${selectedTeamsCount}/${selectedCompetitionsCount})`}
            toggleExpand={toggleExpand}
            expandedContent={
              <>
                <StyledBox>
                  <Typography variant="caption">Teams</Typography>
                  <CustomButton
                    variant="text"
                    label="Reset all"
                    onClick={resetAllTeamsandCompetitions}
                  />
                </StyledBox>

                <ChipDisplay
                  data={displayedTeams}
                  handleRemoveTeam={handleRemoveTeam}
                />
                {selectedTeamsCount > MAX_AMOUNT && (
                  <StyledChipWrapper>
                    <StyledChip
                      onClick={handleOpen}
                      label={`and ${notDisplayedTeamsCount} more`}
                      variant="outlined"
                    />
                  </StyledChipWrapper>
                )}

                <Typography variant="caption">Competitions</Typography>
                {selectedCompetitionsCount > 0 ? (
                  <ChipDisplay
                    data={selectedCompetitions}
                    handleRemoveTeam={handleRemoveCompetition}
                  />
                ) : (
                  <Typography variant="subtitle2" color="text.disabled">
                    No competitions selected
                  </Typography>
                )}

                {open && (
                  <AllSelectedTeamsModal
                    open={open}
                    handleClose={handleClose}
                    handleOpen={handleOpen}
                    content={
                      <ChipDisplay
                        data={selectedTeams}
                        handleRemoveTeam={handleRemoveTeam}
                      />
                    }
                  />
                )}
              </>
            }
          />
          <StyledButtonBox>
            <CustomButton
              onClick={confirmSelectionAndNextStep}
              label="Confirm and go to 2nd step"
              size="large"
              variant="contained"
            />
          </StyledButtonBox>
        </Paper>
      )}
    </StyledSelectedTeams>
  );
};

export default SelectedFavourites;
